/*
 * Copyright © 2020-2023 Ocean Galaxy Inc. All Rights Reserved.
 * @Description:
 * @LastEditors: yongqiang
 * @LastEditTime: 2025-12-29 14:39:00
 */
/* eslint-disable no-undef */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import paymentConfig from '../../../constants/payment'
import alipayIcon from '../../../images/icon_zhifubao.png'
import weChatIcon from '../../../images/icon_weixinzhifu.png'
import sensors from '../../../utils/sensors'
import { useAppDispatch, useAppSelector } from '../../../app/hooks'
import type { RootState } from '../../../app/store'
import { GoodsItem } from './types'
import {
  createBatchPaymentOrder,
  createPaymentOrder,
  fetchPaymentOrderStatus
} from '../../../actions/payment'
import { capturePromiseException } from '../../../utils/sentryUtils'
import { message, notification } from 'antd'
import { fetchGroupInfo } from '../../../actions/group'
import './index.scss'
import { calcCheckedPhotoNum } from '@/pages/components/photoLib/photo'

const { vipKind, channelType, paymentType, alipayUrlPrefix, paymentStatus } =
  paymentConfig

const pollingDelay = 3000
const maxPollingCount = (30 * 60 * 1000) / pollingDelay // 轮询次数上限
const maxQrCodeInvalidCount = (5 * 60 * 1000) / pollingDelay // 二维码失效次数上限

interface PaymentAreaProps {
  selectedGoods: GoodsItem | null
  onClose: (isSensor?: boolean) => void
  sensorClickEvent?: any
  paySource?: string // 支付类型，singleBatchDownload: 单词批量下载
}

const SettlementWithCoupon = ({ selectedGoods, channel, paySource }) => {
  if (['singleBatchDownload'].includes(paySource)) return null
  return (
    <div className="payment-code-text-coupon">
      <div className="original-price">
        <span>原价</span>
        <span>
          ￥{((selectedGoods?.price + 40) / 12).toFixed(1)}/月 X 12月 =&nbsp;
          <strong>￥{selectedGoods?.price + 40}</strong>
        </span>
      </div>
      <div className="coupon-text">
        <span>限时优惠</span>
        <strong>减￥40</strong>
      </div>
      <div className="payment-price">
        <div className="payment-code-tip">
          {channel === channelType.Alipay ? (
            <>
              <img src={alipayIcon} />
              <span>支付宝扫码支付</span>
            </>
          ) : (
            <>
              <img src={weChatIcon} />
              <span>微信扫码支付</span>
            </>
          )}
        </div>
        <div className="payment-code-price">
          <span className="unit">￥</span>
          <span className="num">{selectedGoods?.price}</span>
        </div>
      </div>
    </div>
  )
}

const Settlement = ({ selectedGoods, channel, paySource }) => {
  if (['singleBatchDownload'].includes(paySource)) return null
  return (
    <div className="payment-code-text">
      <div className="payment-code-price">
        <span className="unit">￥</span>
        <span className="num">{selectedGoods?.price}</span>
        <span className="per-month">
          = {selectedGoods?.content.pricePerMonth.toFixed(1)}/月 X{' '}
          {selectedGoods?.content.monthCnt || 12}月
        </span>
      </div>
      <div className="payment-code-tip">
        {channel === channelType.Alipay ? (
          <>
            <img src={alipayIcon} />
            <span>支付宝扫码支付</span>
          </>
        ) : (
          <>
            <img src={weChatIcon} />
            <span>微信扫码支付</span>
          </>
        )}
      </div>
    </div>
  )
}

const BatchDownloadSettlement = ({
  selectedGoods,
  channel,
  counts,
  paySource
}) => {
  const price = selectedGoods.price
  const resultPrice =
    paySource === 'singleBatchDownload'
      ? (Number(price) * 10 * counts) / 10
      : price

  return (
    <div className="batchPayChannelText">
      {channel === channelType.Alipay && (
        <div className="payContainer">
          <img
            width={20}
            height={20}
            style={{
              marginRight: '10px',
              verticalAlign: 'middle',
              marginBottom: '6px'
            }}
            src={alipayIcon}
          />
          <div className="priceText">
            <span>支付宝扫码支付</span>
            <span className="unit">￥</span>
            <span className="num">{resultPrice}</span>
          </div>
        </div>
      )}
      {channel === channelType.WeChat && (
        <div className="payContainer">
          <img
            width={20}
            height={20}
            style={{
              marginRight: '10px',
              verticalAlign: 'middle',
              marginBottom: '6px'
            }}
            src={weChatIcon}
          />
          <div className="priceText">
            <span>微信扫码支付</span>
            <span className="unit">￥</span>
            <span className="num">{resultPrice}</span>
          </div>
        </div>
      )}
    </div>
  )
}

const PaymentArea: React.FC<PaymentAreaProps> = (props) => {
  const { selectedGoods, onClose, sensorClickEvent } = props
  const dispatch = useAppDispatch()
  const groupID = useAppSelector((state: any) => state.group.groupId)
  const sourceDetail = useAppSelector(
    (state: any) => state.main.paymentStatus?.sourceDetail
  )
  const isOnlyInGroup = useAppSelector((state: any) => state.group.onlyInGroup)
  const [alipayUrl, setAlipayUrl] = useState('')
  const [wechatUrl, setWechatUrl] = useState('')
  const [channel, setChannel] = useState(channelType.Alipay)
  const [isCreatingOrder, setIsCreatingOrder] = useState(false) // 团队中其他成员是否正在创建订单（1分钟逻辑）
  const [qrCodeInvalid, setQrCodeInvalid] = useState(false) // 二维码是否失效
  const orderNo = useRef('')
  const orderStatusTimer = useRef<any>(null)
  const notificationTimer = useRef<any>(null)
  const pollingCount = useRef(0)
  const vipType = useMemo(() => {
    return selectedGoods?.content?.vipType || paymentType.multipleYear
  }, [selectedGoods])

  const photoState = useAppSelector((state) => state.photo)
  const checkedPhotos = photoState.checkedPhotos
  const photos = useAppSelector((state) => state.photo.photos)
  const checkAll = useAppSelector((state) => state.photo.checkAll)
  // photov2 选中的照片（暂时兼容，未来将完全使用新版状态）
  const contentCheckedPhotos = useAppSelector(
    (state: RootState) => state.photosByContent?.checkedPhotos
  )
  const contentUncheckedPhotos = useAppSelector(
    (state: RootState) => state.photosByContent?.unCheckedPhotos
  )
  const contentCheckAll = useAppSelector(
    (state: RootState) => state.photosByContent?.checkAll
  )
  const contentPhotosNum = useAppSelector(
    (state: RootState) => state.photosByContent?.photosNum || 0
  )
  const contentCheckedNum = useMemo(() => {
    if (!contentCheckedPhotos && !contentCheckAll) return 0
    if (contentCheckAll) {
      const uncheckedSize = contentUncheckedPhotos?.size ?? 0
      return Math.max(0, contentPhotosNum - uncheckedSize)
    }
    return contentCheckedPhotos?.size ?? 0
  }, [
    contentCheckedPhotos,
    contentCheckAll,
    contentPhotosNum,
    contentUncheckedPhotos
  ])
  const selectPhotosCounts = useMemo(() => {
    if (contentCheckedNum > 0) {
      return contentCheckedNum
    }
    return calcCheckedPhotoNum(photoState)
  }, [contentCheckedNum, photoState])
  const componentUnmounted = useRef(false)
  const exportDownloadPhotoOnceParams = useAppSelector(
    (state) => state.photo.exportDownloadPhotoOnceParams
  )

  useEffect(() => {
    return () => {
      componentUnmounted.current = true
    }
  }, [])

  const clearTimers = useCallback(() => {
    clearInterval(orderStatusTimer.current)
    orderStatusTimer.current = null
    clearInterval(notificationTimer.current)
    notificationTimer.current = null
    notification.destroy()
  }, [orderStatusTimer, notificationTimer])

  const createOrder = (isClickRefresh = false) => {
    if (isClickRefresh) {
      // 点击刷新上报埋点
      const refresh =
        channel === channelType.Alipay
          ? 'refreshAliQRcode'
          : 'refreshWechatQRcode'
      sensorClickEvent(refresh)
    }
    clearTimers()
    pollingCount.current = 0
    if (selectedGoods) {
      let _channel = channel
      if (
        (selectedGoods?.content?.vipType === paymentType.multipleMonth ||
          selectedGoods?.content?.vipType === paymentType.multipleYear ||
          selectedGoods?.content?.vipType === paymentType.periodQuarter) &&
        channel === channelType.WeChat
      ) {
        setChannel(channelType.Alipay)
        _channel = channelType.Alipay
      }

      // 单次批量下载图片
      if (props.paySource === 'singleBatchDownload') {
        handleBatchDownloadCreateOrder({
          groupID: groupID,
          goodsInfo: [
            {
              goodsID: selectedGoods.id,
              cnt: selectPhotosCounts
            }
          ],
          channel: _channel,
          sourceDetail
        })
        return
      }
      const params: any = {
        goodsID: selectedGoods.id,
        channel: _channel,
        groupID: groupID,
        sourceDetail
      }
      dispatch(createPaymentOrder(params))
        .then((data) => {
          if (data.status === 0) {
            const isWechat = _channel === channelType.WeChat ? true : false
            if (isWechat) {
              setWechatUrl(data.wechat.qrcodeUrl)
              setAlipayUrl('')
            } else {
              setAlipayUrl(alipayUrlPrefix + data.alipay.requestBody)
              setWechatUrl('')
            }
            setIsCreatingOrder(false)
            setQrCodeInvalid(false)
            orderNo.current = data.orderNo
            if (props.paySource === 'singleBatchDownload') {
              localStorage.setItem('orderNo', data.orderNo)
            }
            pollingOrderStatus()
          } else if (data.status === 9) {
            message.error('其他成员正在创建订单')
            setIsCreatingOrder(true)
          } else {
            message.error(data.msg)
            setIsCreatingOrder(false)
            setAlipayUrl('')
            setWechatUrl('')
          }
        })
        .catch(capturePromiseException)
    }
  }

  const pollingOrderStatus = () => {
    if (orderStatusTimer.current) {
      clearInterval(orderStatusTimer.current)
      orderStatusTimer.current = null
    }
    orderStatusTimer.current = setInterval(() => {
      componentUnmounted.current && clearInterval(orderStatusTimer.current)
      pollingCount.current++
      handleFetchOrderStatus()
    }, pollingDelay)
  }

  const orderNotification = (type: 'info' | 'error' | 'warning') => {
    notification.destroy()
    clearInterval(notificationTimer.current)
    notificationTimer.current = null

    const initConfig = paymentConfig.initConfig
    const infoConfig = paymentConfig.infoConfig
    const errorConfig = paymentConfig.errorConfig(this, notification)
    const warningConfig = paymentConfig.warningConfig(this)

    if (type === 'info') {
      notification.info({ ...initConfig, ...infoConfig })
    } else if (type === 'error') {
      notification.error({ ...initConfig, ...errorConfig })
    } else if (type === 'warning') {
      notification.warning({ ...initConfig, ...warningConfig })
    }

    // 倒计时通知框，轮询
    if (type === 'info') {
      const initTime = 30
      let time = 30
      notificationTimer.current = setInterval(() => {
        componentUnmounted.current && clearInterval(notificationTimer.current)
        if ((initTime - time) % (pollingDelay / 1000) === 0) {
          handleFetchOrderStatus(true)
        }
        if (--time >= 1) {
          notification.info({
            ...initConfig,
            message: '请稍后，正在查询订单状态（' + time + 's）',
            description: '请您耐心等待'
          })
        } else {
          clearInterval(notificationTimer.current)
          notificationTimer.current = null
          orderNotification('warning')
        }
      }, 1000)
    }
  }

  const handlePaymentSuccess = useCallback(() => {
    sensors.track('web_get_purchase_vip_result', {
      result: 'suc',
      groupID
    })
    clearTimers()
    message.success('支付成功!')
    dispatch(
      fetchGroupInfo({
        group_id: groupID,
        only_in_group: isOnlyInGroup
      })
    ).then(() => {
      onClose()
    })
  }, [groupID, isOnlyInGroup, clearTimers, onClose])

  const handleFetchOrderStatus = useCallback(
    (isAgainQuery = false) => {
      if (pollingCount.current >= maxQrCodeInvalidCount) {
        setWechatUrl('')
        setAlipayUrl('')
        setQrCodeInvalid(true)
        if (pollingCount.current >= maxPollingCount) {
          clearInterval(orderStatusTimer.current)
          orderStatusTimer.current = null
        }
      }
      dispatch(
        fetchPaymentOrderStatus({
          orderNo: orderNo.current,
          isBatch: props.paySource === 'singleBatchDownload' // 如果是批量购买则传递 true
        })
      )
        .then((data) => {
          if (data.status === 0) {
            //连续包年包月商品
            if (
              vipType === paymentConfig.paymentType.multipleMonth ||
              vipType === paymentConfig.paymentType.multipleYear ||
              vipType === paymentConfig.paymentType.periodQuarter
            ) {
              if (data.payStatus !== paymentStatus.UnSign.code) {
                //已签约关闭支付弹层
                onClose()
                // 清除轮询定时器
                clearInterval(orderStatusTimer.current)
                orderStatusTimer.current = null
              }
              if (data.payStatus === paymentStatus.Paid.code) {
                //支付成功
                handlePaymentSuccess()
              } else if (data.payStatus === paymentStatus.PaymentFail.code) {
                //支付失败
                sensors.track('web_get_purchase_vip_result', {
                  result: 'fail',
                  groupID
                })
                orderNotification('error')
              } else if (data.payStatus === paymentStatus.Unpaid.code) {
                // 通知框触发轮询，不进行重新调用通知框（循环）
                if (!isAgainQuery) {
                  orderNotification('info')
                }
              }
            } else {
              // 单独包月包年商品
              // 清除轮询定时器
              if (data.payStatus !== paymentStatus.Unpaid.code) {
                clearInterval(orderStatusTimer.current)
                orderStatusTimer.current = null
              }

              if (data.payStatus === paymentStatus.Paid.code) {
                //支付成功
                handlePaymentSuccess()
              } else if (data.payStatus === paymentStatus.PaymentFail.code) {
                sensors.track('web_get_purchase_vip_result', {
                  result: 'fail',
                  groupID
                })
                // 二维码失效
                setAlipayUrl('')
                setWechatUrl('')
                message.success('支付失败!')
              }
            }
          }
        })
        .catch(capturePromiseException)
    },
    [pollingCount, vipType, groupID, clearTimers, onClose, handlePaymentSuccess]
  )

  useEffect(() => {
    if (selectedGoods) {
      createOrder()
    }
  }, [selectedGoods, channel])

  useEffect(() => {
    return clearTimers
  }, [])

  /**
   * 单次批量下载图片创建订单
   * */
  const handleBatchDownloadCreateOrder = (params) => {
    params.extra = {
      exportPayload: exportDownloadPhotoOnceParams
    }
    dispatch(createBatchPaymentOrder(params))
      .then((data) => {
        if (data.status === 0) {
          const isWechat = params.channel === channelType.WeChat ? true : false
          if (isWechat) {
            setWechatUrl(data.wechat.qrcodeUrl)
            setAlipayUrl('')
          } else {
            setAlipayUrl(alipayUrlPrefix + data.alipay.requestBody)
            setWechatUrl('')
          }
          setIsCreatingOrder(false)
          setQrCodeInvalid(false)
          orderNo.current = data.orderNo
          localStorage.setItem('orderNo', data.orderNo)
          pollingOrderStatus()
        } else if (data.status === 9) {
          message.error('其他成员正在创建订单')
          setIsCreatingOrder(true)
        } else {
          message.error(data.msg)
          setIsCreatingOrder(false)
          setAlipayUrl('')
          setWechatUrl('')
        }
      })
      .catch(capturePromiseException)
  }

  return (
    <div className="payment-type">
      <div className="payment-wrapper">
        <div className="payment-tabs">
          <div
            className={
              'payment-tab ' + (channel === channelType.Alipay ? 'active' : '')
            }
            onClick={() => {
              if (channel !== channelType.Alipay) {
                sensorClickEvent('aliPayment')
                setChannel(channelType.Alipay)
              }
            }}
          >
            <img src={alipayIcon} />
            <span>支付宝</span>
          </div>
          {(vipType === paymentType.singleMonth ||
            vipType === paymentType.singleYear ||
            vipType === paymentType.twoYear ||
            vipType === paymentType.batchDownload) && (
            <div
              className={
                'payment-tab ' +
                (channel === channelType.WeChat ? 'active' : '')
              }
              onClick={() => {
                if (channel !== channelType.WeChat) {
                  sensorClickEvent('wechatPayment')
                  setChannel(channelType.WeChat)
                }
              }}
            >
              <img src={weChatIcon} />
              <span>微信支付</span>
            </div>
          )}
        </div>
        <div className="payment-code">
          <div className="payment-code-img">
            <div className="payment-default-text">
              <div className="invalid-code">即将显示付款码...</div>
            </div>
            {isCreatingOrder && (
              <div className="payment-default-text zindex">
                <div className="invalid-code">请稍后再试</div>
              </div>
            )}
            {qrCodeInvalid && (
              <div className="payment-default-code">
                <div className="invalid-code">二维码已失效</div>
                <div
                  className="update-code"
                  onClick={() => {
                    createOrder(true)
                  }}
                >
                  点击刷新
                </div>
              </div>
            )}
            {wechatUrl && (
              <div className="payment-wechat">
                <img src={wechatUrl} alt="weChat-code" />
              </div>
            )}
            {alipayUrl &&
              (vipType === paymentType.multipleYear ||
                vipType === paymentType.multipleMonth ||
                vipType === paymentType.periodQuarter) && (
                <div className="payment-alipay">
                  {alipayUrl && (
                    <div className="iframe-container">
                      <div className="iframe-wrapper">
                        <iframe
                          id="alipay"
                          name="alipay"
                          scrolling="no"
                          // border="none"
                          src={alipayUrl}
                        >
                          <p>你的浏览器不支持iframes。</p>
                        </iframe>
                      </div>
                    </div>
                  )}
                </div>
              )}
            {alipayUrl &&
              (vipType === paymentType.singleMonth ||
                vipType === paymentType.singleYear ||
                vipType === paymentType.twoYear ||
                vipType === paymentType.batchDownload) && (
                <div className="payment-alipay">
                  {alipayUrl && (
                    <div className="iframe-container iframe-container-single">
                      <div className="iframe-wrapper">
                        <iframe
                          id="alipay-single"
                          name="alipay-single"
                          scrolling="no"
                          // border="none"
                          src={alipayUrl}
                        >
                          <p>你的浏览器不支持iframes。</p>
                        </iframe>
                      </div>
                    </div>
                  )}
                </div>
              )}
          </div>
          {selectedGoods?.content?.vipKind === vipKind.vip &&
          selectedGoods?.content?.vipType === paymentType.multipleYear ? (
            <SettlementWithCoupon
              selectedGoods={selectedGoods}
              channel={channel}
              paySource={props.paySource}
            />
          ) : (
            <Settlement
              selectedGoods={selectedGoods}
              channel={channel}
              paySource={props.paySource}
            />
          )}
          {props.paySource === 'singleBatchDownload' && (
            <BatchDownloadSettlement
              selectedGoods={selectedGoods}
              counts={selectPhotosCounts}
              channel={channel}
              paySource={props.paySource}
            />
          )}
        </div>
      </div>
    </div>
  )
}

export default PaymentArea
